import { useState, useEffect } from "react";
import { EditOutlined, SaveOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { useUpdateSegment } from "../hooks/useRateCard";

/**
 * Rate by portfolio size — where a per-mark segment's price actually lives.
 *
 * The tiers below set seats, free clients and the band's name; none of them set
 * the rate. A quote takes its rate from here by trademark count, so this is the
 * one place an attorney price is raised or cut.
 *
 * The last step is open-ended by design: something has to cover the largest book
 * anyone walks in with.
 */

const num = (n) => (n == null ? "" : Number(n).toLocaleString("en-IN"));

const cell = {
  padding: "8px 11px", borderRadius: 8, border: "1px solid #e2e6f0", fontSize: 13,
  width: "100%", fontVariantNumeric: "tabular-nums",
};
const btn = (color = "#475569", bg = "#fff", border = "#e5e7eb") => ({
  display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 14px",
  borderRadius: 9, fontSize: 12.5, fontWeight: 700, cursor: "pointer",
  border: `1px solid ${border}`, background: bg, color,
});

const TaperEditor = ({ segment, mayEdit }) => {
  const save = useUpdateSegment();
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");

  const taper = segment?.taper;

  useEffect(() => {
    if (!editing) {
      setRows((taper || []).map((s) => ({
        up_to: s.up_to == null ? "" : String(s.up_to),
        rate: s.rate == null ? "" : String(s.rate),
      })));
      setError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(taper), editing]);

  if (!Array.isArray(taper) || !taper.length) return null;

  const setRow = (i, patch) => {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
    setError("");
  };
  const addRow = () => setRows((prev) => [...prev, { up_to: "", rate: "" }]);
  const removeRow = (i) => setRows((prev) => prev.filter((_, idx) => idx !== i));

  const submit = () => {
    const cleaned = rows.filter((r) => String(r.rate).trim() !== "");
    if (!cleaned.length) return setError("A segment needs at least one rate.");

    /* Exactly one step may be open-ended, or the largest portfolios fall through
       to no rate at all — and two would make the order ambiguous. */
    const openEnded = cleaned.filter((r) => String(r.up_to).trim() === "");
    if (openEnded.length !== 1) {
      return setError("Leave the top band's 'up to' blank — exactly one band covers everything above.");
    }
    if (cleaned.some((r) => Number(r.rate) <= 0)) return setError("Every band needs a rate above zero.");

    save.mutate(
      {
        id: segment.id,
        taper: cleaned.map((r) => ({
          up_to: String(r.up_to).trim() === "" ? null : Number(r.up_to),
          rate: Number(r.rate),
        })),
      },
      { onSuccess: () => setEditing(false) },
    );
  };

  return (
    <div style={{ background: "white", borderRadius: 16, border: "1px solid #f0f0f0", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", marginBottom: 20, padding: "16px 20px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <span style={{ fontSize: 11.5, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>
          Rate by portfolio size
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {editing ? (
            <>
              <button onClick={() => setEditing(false)} disabled={save.isPending} style={btn()}>
                Cancel
              </button>
              <button onClick={submit} disabled={save.isPending} style={btn("#fff", "#1d4ed8", "#1d4ed8")}>
                <SaveOutlined /> {save.isPending ? "Saving…" : "Save rates"}
              </button>
            </>
          ) : mayEdit ? (
            <button onClick={() => setEditing(true)} style={btn("#1d4ed8", "#eff6ff", "#bfdbfe")}>
              <EditOutlined /> Edit rates
            </button>
          ) : null}
        </div>
      </div>

      {editing ? (
        <>
          <div style={{ display: "grid", gap: 8 }}>
            {rows.map((r, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: 12.5, color: "#6b7280", width: 46 }}>up to</span>
                <input
                  type="number" min="1" value={r.up_to}
                  onChange={(e) => setRow(i, { up_to: e.target.value })}
                  placeholder="blank = and above"
                  style={{ ...cell, width: 170 }}
                />
                <span style={{ fontSize: 12.5, color: "#6b7280" }}>marks →</span>
                <span style={{ fontSize: 13, color: "#6b7280" }}>₹</span>
                <input
                  type="number" min="0" step="0.01" value={r.rate}
                  onChange={(e) => setRow(i, { rate: e.target.value })}
                  placeholder="144"
                  style={{ ...cell, width: 120 }}
                />
                <span style={{ fontSize: 12.5, color: "#9ca3af" }}>/mark/yr</span>
                <button
                  onClick={() => removeRow(i)}
                  title="Remove this band"
                  style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid #fecaca", background: "#fef2f2", color: "#dc2626", cursor: "pointer" }}
                >
                  <DeleteOutlined />
                </button>
              </div>
            ))}
          </div>
          <button onClick={addRow} style={{ ...btn(), marginTop: 12, borderStyle: "dashed" }}>
            <PlusOutlined /> Add band
          </button>
          <div style={{ fontSize: 11.5, color: "#9ca3af", marginTop: 10, lineHeight: 1.5 }}>
            Leave the top band's <b>up to</b> blank — that one covers every portfolio above
            the rest. Saving re-prices every quote from the next keystroke on.
          </div>
          {error && <div style={{ color: "#dc2626", fontSize: 12.5, marginTop: 8 }}>{error}</div>}
        </>
      ) : (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {taper.map((step, i) => (
              <span
                key={i}
                style={{ display: "inline-flex", alignItems: "baseline", gap: 8, padding: "7px 14px", borderRadius: 10, border: "1px solid #e5e7eb", background: "#fafbff", fontSize: 13 }}
              >
                <span style={{ color: "#6b7280" }}>
                  {step.up_to == null ? "above" : `up to ${num(step.up_to)}`} marks
                </span>
                <b style={{ color: "#111827", fontVariantNumeric: "tabular-nums" }}>
                  ₹{Number(step.rate).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                  <span style={{ color: "#9ca3af", fontWeight: 400 }}> /mark/yr</span>
                </b>
              </span>
            ))}
          </div>
          <div style={{ fontSize: 11.5, color: "#9ca3af", marginTop: 10, lineHeight: 1.5 }}>
            A quote's rate comes from this taper, by trademark count. The tiers below
            set the seats, the free clients and the name — not the rate.
          </div>
        </>
      )}
    </div>
  );
};

export default TaperEditor;
